import React, { createContext, type ReactNode, useContext, useMemo } from 'react';

import type { BaseToastDefinition, ToastStore } from './toast-store';

export interface ToastContextValue<TToastDefinition extends BaseToastDefinition> {
  toastStore: ToastStore<TToastDefinition>;
}

const ToastContext = createContext<ToastContextValue<BaseToastDefinition> | undefined>(undefined);

export interface ToastContextProviderProps<TToastDefinition extends BaseToastDefinition> {
  children: ReactNode;
  toastStore: ToastStore<TToastDefinition>;
}

const ToastContextProvider = <TToastDefinition extends BaseToastDefinition>({
  children,
  toastStore,
}: ToastContextProviderProps<TToastDefinition>) => {
  // memoize the context value so that it doesn't change on every render
  // (though ideally this component should rarely, if ever, re-render)
  const contextValue: ToastContextValue<TToastDefinition> = useMemo(() => {
    return {
      toastStore,
    };
  }, [toastStore]);

  return (
    <ToastContext.Provider
      // We cast the context value to `unknown` and then back to `ToastContextValue`
      // to avoid a TypeScript error saying that:
      // 'BaseToastDefinition' is assignable to the constraint of type 'TToastDefinition',
      // but 'TToastDefinition' could be instantiated with a different subtype
      // of constraint 'BaseToastDefinition'.
      // We know that the context value is of type `ToastContextValue<TToastDefinition>`
      // because we just created it above. And we know that `TToastDefinition` is a subtype
      // of `BaseToastDefinition` because of the generic constraint on this component.
      // And that's really the only thing we want to guarantee at this point, but TS
      // won't let us do that.
      value={contextValue as unknown as ToastContextValue<BaseToastDefinition>}
    >
      {children}
    </ToastContext.Provider>
  );
};

export default ToastContextProvider;

/**
 * IMPORTANT: Be sure when using this hook that the nearest `ToastContextProvider`
 * in the component tree provides a value matching the type you specify for `TToastDefinition`.
 */
export function useToastContext<TToastDefinition extends BaseToastDefinition>() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within a ToastContextProvider');
  }

  // Caution: This is not a safe type assertion. It's up to the consumer to ensure
  // that the `TToastDefinition` type matches the type provided by the nearest
  // `ToastContextProvider`.
  return context as unknown as ToastContextValue<TToastDefinition>;
}
